/**
 * CityDrawer — painel dentro do mapa com os números da cidade clicada: total de
 * cada métrica e as origens, referers, dispositivos, páginas, campanhas, mídias e
 * palavras-chave dela (da métrica ativa). Clicar num valor aplica esse valor como
 * filtro no mapa; clicar numa métrica troca o que o mapa pinta.
 */

import Icon from './icons.jsx'
import MetricCards from './MetricCards.jsx'
import { MAPA_METRICS, originLabel } from '../lib/mapaFilters.js'

// Ordem das quebras no painel.
const SECTIONS = [
  { key: 'origem', title: 'Origens' },
  { key: 'referer', title: 'Referers' },
  { key: 'dispositivo', title: 'Dispositivos' },
  { key: 'pagina', title: 'Páginas' },
  { key: 'campanha', title: 'Campanhas' },
  { key: 'midia', title: 'Mídias' },
  { key: 'palavra', title: 'Palavras-chave' },
]

const nf = new Intl.NumberFormat('pt-BR')
const VISIBLE_ROWS = 6 // acima disso a lista rola por dentro

const rowLabel = (dim, option) => (dim === 'origem' ? originLabel(option.value) : option.label)

export default function CityDrawer({
  city, data, loading, error, metric, metricLabel, onMetricChange, onApplyFilter, onClose,
}) {
  const total = data?.total ?? 0
  return (
    <aside className="mapa-drawer" aria-label={`Detalhes de ${city.name}`}>
      <header className="mapa-drawer-head">
        <div className="mapa-drawer-title">
          <strong>{city.name}</strong>
          <span className="bw-map-tt-uf">{city.uf}</span>
        </div>
        <button type="button" className="mapa-drawer-close" onClick={onClose} aria-label="Fechar detalhes da cidade">
          <Icon n="x" s={14} />
        </button>
      </header>

      <p className="mapa-drawer-sub">
        <strong>{nf.format(total)}</strong> {metricLabel} na cidade
        {loading && <span className="mapa-drawer-loading"> · atualizando...</span>}
      </p>

      {error && <p className="mapa-drawer-error">Erro ao carregar: {error}</p>}

      <section className="mapa-drawer-block">
        <h4 className="mf-title">Métricas</h4>
        <MetricCards
          inline
          list
          active={metric}
          onChange={onMetricChange}
          values={data?.totals}
          items={MAPA_METRICS}
        />
      </section>

      {SECTIONS.map(({ key, title }) => {
        const rows = data?.breakdowns?.[key] || []
        return (
          <section className="mapa-drawer-block" key={key}>
            <h4 className="mf-title">
              {title}
              {rows.length > VISIBLE_ROWS && <span className="mapa-drawer-count"> ({rows.length})</span>}
            </h4>
            {rows.length === 0 && <p className="mapa-drawer-empty">{loading ? 'Carregando...' : 'Sem dados'}</p>}
            <div className={`mapa-drawer-list${rows.length > VISIBLE_ROWS ? ' is-scroll' : ''}`}>
            {rows.map((o) => {
              const pct = total > 0 ? Math.min(100, (o.count / total) * 100) : 0
              const label = rowLabel(key, o)
              return (
                <button
                  key={o.value}
                  type="button"
                  className="mapa-drawer-row"
                  title={`${label} — clique para filtrar o mapa`}
                  onClick={() => onApplyFilter(key, o.value)}
                >
                  <span className="mapa-drawer-row-head">
                    <span className="mapa-drawer-row-name">{label}</span>
                    <span className="mapa-drawer-row-count">
                      {nf.format(o.count)} <small>{pct.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%</small>
                    </span>
                  </span>
                  <span className="bw-bar-track"><span className="bw-bar-fill" style={{ width: `${pct}%` }} /></span>
                </button>
              )
            })}
            </div>
          </section>
        )
      })}
    </aside>
  )
}
